import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { z } from 'zod';

import type { SourceAutodocItem } from '@/api/types/node';
import { type AutodocLink, AutodocsTable } from '@/components/Autodocs/AutodocsTable';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { useVersions } from '@/contexts/VersionsContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { searchParam } from '@/utils/router';

const autodocsSearchSchema = z.object({
  pageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  pageIndex: z.number().default(0).catch(0),
  search: searchParam().optional(),
  type: z.enum(['all', 'autodoc', 'page']).optional(),
  tag_ids: z.array(z.string()).optional(),
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
  sorting: z
    .array(
      z.object({
        id: z.string(),
        desc: z.boolean(),
      })
    )
    .default([{ id: 'updated_at', desc: true }])
    .catch([{ id: 'updated_at', desc: true }]),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/autodocs/')({
  component: AutodocsListPage,
  validateSearch: autodocsSearchSchema,
});

function AutodocsListPage() {
  const { captureEvent } = useAnalytics();
  const { codebaseId, organizationName } = useCodebaseRoute();
  const { versionId } = Route.useSearch();

  const { hasBranches, isVersionsLoading, rootVersionNodeId, selectedBranchId, sourceId } = useVersions();

  useEffect(() => {
    captureEvent('autodocs_list_viewed', { codebase_id: codebaseId });
  }, [captureEvent, codebaseId]);

  const getAutodocLink = useCallback(
    (item: SourceAutodocItem): AutodocLink => {
      const branchId = hasBranches ? selectedBranchId : undefined;
      const search =
        branchId || versionId ? { ...(branchId ? { branchId } : {}), ...(versionId ? { versionId } : {}) } : undefined;
      if (item.type === 'autodoc') {
        return {
          to: '/$organizationName/cb/$codebaseId/autodocs/$autodocId',
          params: { autodocId: item.id, codebaseId, organizationName },
          search,
        };
      }
      return {
        to: '/$organizationName/cb/$codebaseId/autodocs/page/$pageVersionNodeId',
        params: { codebaseId, organizationName, pageVersionNodeId: item.rootVersionNodeId || '' },
        search,
      };
    },
    [codebaseId, hasBranches, organizationName, selectedBranchId, versionId]
  );

  return (
    <AutodocsTable
      getAutodocLink={getAutodocLink}
      emptyStateDescription="Generate autodocs to get AI-powered documentation for your codebase."
      isSourceLoading={isVersionsLoading}
      route={Route}
      versionNodeId={rootVersionNodeId}
      sourceId={sourceId}
    />
  );
}
